import { Switch, Route, useLocation, Redirect } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/components/ThemeProvider";
import { AuthProvider } from "@/contexts/AuthContext";
import { TerritoryProvider } from "@/contexts/TerritoryContext";
import { AccessibilityProvider } from "@/contexts/AccessibilityContext";
import { TopNavigation } from "@/components/TopNavigation";
import { ThemeToggle } from "@/components/ThemeToggle";
import { StudyBuddy } from "@/components/StudyBuddy";
import { SupportWidget } from "@/components/SupportWidget";
import { ProtectedAdminRoute } from "@/components/ProtectedAdminRoute";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AuthOnlyRoute } from "@/components/AuthOnlyRoute";
import Home from "@/pages/Home";
import Signup from "@/pages/Signup";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ClaimAccount from "@/pages/ClaimAccount";
import FindAccount from "@/pages/FindAccount";
import Dashboard from "@/pages/Dashboard";
import StudyPlan from "@/pages/StudyPlan";
import Learning from "@/pages/Learning";
import LessonPlayer from "@/pages/LessonPlayer";
import LessonLibrary from "@/pages/LessonLibrary";
import PerformanceTasks from "@/pages/PerformanceTasks";
import PerformanceTaskPlayer from "@/pages/PerformanceTaskPlayer";
import AdminPerformanceTasks from "@/pages/AdminPerformanceTasks";
import AdminLessons from "@/pages/AdminLessons";
import AdminMoat from "@/pages/AdminMoat";
import AdminSignIn from "@/pages/AdminSignIn";
import AdminCourses from "@/pages/AdminCourses";
import AdminComms from "@/pages/AdminComms";
import TestResults from "@/pages/TestResults";
import Messages from "@/pages/Messages";
import Notifications from "@/pages/Notifications";
import UserProfile from "@/pages/UserProfile";
import AdminLayout from "@/pages/AdminLayout";
import AdminHome from "@/pages/AdminHome";
import AdminSection from "@/pages/AdminDashboard";
import AdminSubscriptions from "@/pages/AdminSubscriptions";
import AdminRevenue from "@/pages/AdminRevenue";
import AdminMetrics from "@/pages/AdminMetrics";
import AdminAcquisition from "@/pages/AdminAcquisition";
import AdminOpenemis from "@/pages/AdminOpenemis";
import AdminSignupAttribution from "@/pages/AdminSignupAttribution";
import AdminStats from "@/pages/AdminStats";
import AdminUsersManagement from "@/pages/AdminUsersManagement";
import DiscountCodeManager from "@/pages/DiscountCodeManager";
import ReferralPayoutManager from "@/pages/ReferralPayoutManager";
import BrevoMappingManager from "@/pages/BrevoMappingManager";
import CurriculumManager from "@/pages/CurriculumManager";
import ReadingMaterialsManager from "@/pages/ReadingMaterialsManager";
import AdminQuestionGeneration from "@/pages/AdminQuestionGeneration";
import QuizzativeQuestions from "@/pages/QuizzativeQuestions";
import AcademicCalendarManager from "@/pages/AcademicCalendarManager";
import QuestionSandbox from "@/pages/QuestionSandbox";
import AdminQuestionReports from "@/pages/AdminQuestionReports";
import AdminApiKeys from "@/pages/AdminApiKeys";
import LearningMaterialsManager from "@/pages/LearningMaterialsManager";
import WhatsAppManager from "@/pages/WhatsAppManager";
import WhatsAppInbox from "@/pages/WhatsAppInbox";
import AdminBanners from "@/pages/AdminBanners";
import AdminKnowledgeBase from "@/pages/AdminKnowledgeBase";
import AdminStripePricing from "@/pages/AdminStripePricing";
import AdminParents from "@/pages/AdminParents";
import QuizzativeExportSync from "@/pages/QuizzativeExportSync";
import QuizzativeIntegration from "@/pages/QuizzativeIntegration";
import QuizzativeApiLogs from "@/pages/QuizzativeApiLogs";
import QuizzativeBulkExport from "@/pages/QuizzativeBulkExport";
import Subscribe from "@/pages/Subscribe";
import PepCheck from "@/pages/PepCheck";
import PepPrep from "@/pages/PepPrep";
import ProfileSettings from "@/pages/ProfileSettings";
import MyProfile from "@/pages/MyProfile";
import Preferences from "@/pages/Preferences";
import Privacy from "@/pages/Privacy";
import TermsConditions from "@/pages/TermsConditions";
import ChallengeQuiz from "@/pages/ChallengeQuiz";
import ChallengeResults from "@/pages/ChallengeResults";
import Onboarding from "@/pages/Onboarding";
import ClaimPrize from "@/pages/ClaimPrize";
import PartnerLogin from "@/pages/PartnerLogin";
import PartnerSignup from "@/pages/PartnerSignup";
import PartnerDashboard from "@/pages/PartnerDashboard";
import ParentLogin from "@/pages/ParentLogin";
import ParentDashboardPage from "@/pages/ParentDashboardPage";
import ParentChildDetail from "@/pages/ParentChildDetail";
import ParentChildren from "@/pages/ParentChildren";
import ParentSettings from "@/pages/ParentSettings";
import ParentBilling from "@/pages/ParentBilling";
import ParentNotifications from "@/pages/ParentNotifications";
import ProgressReport from "@/pages/ProgressReport";
import PepResults from "@/pages/PepResults";
import Courses from "@/pages/Courses";
import CourseDetail from "@/pages/CourseDetail";
import CredentialPage from "@/pages/CredentialPage";
import Leaderboard from "@/pages/Leaderboard";
import ViewAllBadges from "@/pages/ViewAllBadges";
import ReadingLibrary from "@/pages/ReadingLibrary";
import ReadingView from "@/pages/ReadingView";
import AdminXPDecay from "@/pages/AdminXPDecay";
import AdminPepPredictions from "@/pages/AdminPepPredictions";
import AdminCreatorApplications from "@/pages/AdminCreatorApplications";
import AdminCreatorVideos from "@/pages/AdminCreatorVideos";
import AdminFeedback from "@/pages/AdminFeedback";
import AdminPaymentAnalytics from "@/pages/AdminPaymentAnalytics";
import AdminLeaderboard from "@/pages/AdminLeaderboard";
import AdminUserMessages from "@/pages/AdminUserMessages";
import AdminAnalytics from "@/pages/AdminAnalytics";
import AdminVouchers from "@/pages/AdminVouchers";
import CreatorCompleteSignup from "@/pages/CreatorCompleteSignup";
import CreatorCourses from "@/pages/CreatorCourses";
import PracticePapers from "@/pages/PracticePapers";
import PaperDownloadSuccess from "@/pages/PaperDownloadSuccess";
import NotFound from "@/pages/not-found";
import SummerInterest from "@/pages/SummerInterest";
import ApiDocs from "@/pages/ApiDocs";
import FollowsPage from "@/pages/FollowsPage";
import ActivityFeed from "@/pages/ActivityFeed";
import TrialLink from "@/pages/TrialLink";
import ContactPage from "@/pages/ContactPage";
import FeaturesPage from "@/pages/FeaturesPage";
import HowItWorksPage from "@/pages/HowItWorksPage";
import { MobileBottomNav } from "@/components/MobileBottomNav";
import { ReferralTracker } from "@/components/ReferralTracker";
import { LiveActivityToast } from "@/components/LiveActivityToast";
import { UsernameSelectionModal } from "@/components/UsernameSelectionModal";
import { isEmbedded } from "@/lib/embedded";
import { AnnouncementModal } from "@/components/AnnouncementModal";
import { GradeMigrationModal } from "@/components/GradeMigrationModal";
import { PwaInstallModal } from "@/components/PwaInstallModal";
import { PushPermissionModal } from "@/components/PushPermissionModal";
import AdminAnnouncements from "@/pages/AdminAnnouncements";
import AdminLiveClasses from "@/pages/AdminLiveClasses";
import Classes from "@/pages/Classes";
import ClassDetail from "@/pages/ClassDetail";
import { useAuth } from "@/contexts/AuthContext";
import { WebSocketProvider } from "@/contexts/WebSocketContext";
import { useTheme } from "@/components/ThemeProvider";
import { useEffect, useRef, useState } from "react";
import { initMixpanel } from "@/lib/mixpanel";
import { initPostHog, capturePostHogPageview } from "@/lib/posthog";
import { initMetaPixel, metaPageView } from "@/lib/metaPixel";

// Syncs theme preference to/from server so it persists across devices
function PreferencesSyncer() {
  const { user } = useAuth();
  const { theme, setTheme } = useTheme();
  const prevUserIdRef = useRef<string | null>(null);
  const isMountingRef = useRef(true);

  // On login: load server theme and apply it
  useEffect(() => {
    if (!user) { prevUserIdRef.current = null; return; }
    if (user.id === prevUserIdRef.current) return;
    prevUserIdRef.current = user.id;

    fetch('/api/user/ui-preferences')
      .then(r => r.ok ? r.json() : null)
      .then(prefs => {
        if (prefs?.theme && prefs.theme !== theme) {
          // Server has a stored preference: apply it (cross-device sync)
          setTheme(prefs.theme);
        } else if (!prefs?.theme && theme !== 'system') {
          // Server has no preference yet: upload current local preference
          fetch('/api/user/ui-preferences', {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ theme }),
          }).catch(() => {});
        }
      })
      .catch(() => {});
  }, [user?.id]);

  // On theme change by user: save to server (skip on initial mount)
  useEffect(() => {
    if (isMountingRef.current) { isMountingRef.current = false; return; }
    if (!user) return;
    fetch('/api/user/ui-preferences', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ theme }),
    }).catch(() => {});
  }, [theme]);

  return null;
}

function AdminRouter() {
  return (
    <ProtectedAdminRoute>
      <AdminLayout>
        <Switch>
          <Route path="/admin">
            <AdminHome />
          </Route>
          <Route path="/admin/question-analytics">
            <div className="p-6 space-y-6"><AdminSection section="question-analytics" /></div>
          </Route>
          <Route path="/admin/question-generation">
            <div className="p-6 space-y-6"><AdminQuestionGeneration /></div>
          </Route>
          <Route path="/admin/quizzative-questions">
            <div className="p-6 space-y-6"><QuizzativeQuestions /></div>
          </Route>
          <Route path="/admin/quizzative-export">
            <div className="p-6 space-y-6"><QuizzativeExportSync /></div>
          </Route>
          <Route path="/admin/quizzative-integration">
            <div className="p-6 space-y-6"><QuizzativeIntegration /></div>
          </Route>
          <Route path="/admin/quizzative-api-logs">
            <div className="p-6 space-y-6"><QuizzativeApiLogs /></div>
          </Route>
          <Route path="/admin/quizzative-bulk-export">
            <div className="p-6 space-y-6"><QuizzativeBulkExport /></div>
          </Route>
          <Route path="/admin/openemis">
            <div className="p-6 space-y-6"><AdminOpenemis /></div>
          </Route>
          <Route path="/admin/signup-attribution">
            <div className="p-6 space-y-6"><AdminSignupAttribution /></div>
          </Route>
          <Route path="/admin/sandbox">
            <div className="p-6 space-y-6"><QuestionSandbox /></div>
          </Route>
          <Route path="/admin/stats">
            <div className="p-6 space-y-6"><AdminStats /></div>
          </Route>
          <Route path="/admin/users">
            <div className="p-6 space-y-6"><AdminUsersManagement /></div>
          </Route>
          <Route path="/admin/subscriptions">
            <div className="p-6 space-y-6"><AdminSubscriptions /></div>
          </Route>
          <Route path="/admin/revenue">
            <div className="p-6 space-y-6"><AdminRevenue /></div>
          </Route>
          <Route path="/admin/metrics">
            <div className="p-6 space-y-6"><AdminMetrics /></div>
          </Route>
          <Route path="/admin/acquisition">
            <div className="p-6 space-y-6"><AdminAcquisition /></div>
          </Route>
          <Route path="/admin/payment-analytics">
            <div className="p-6 space-y-6"><AdminPaymentAnalytics /></div>
          </Route>
          <Route path="/admin/leaderboard">
            <div className="p-6 space-y-6"><AdminLeaderboard /></div>
          </Route>
          <Route path="/admin/user-messages">
            <div className="p-6 space-y-6"><AdminUserMessages /></div>
          </Route>
          <Route path="/admin/analytics">
            <div className="p-6 space-y-6"><AdminAnalytics /></div>
          </Route>
          <Route path="/admin/cancellation-feedback">
            <div className="p-6 space-y-6"><AdminSection section="cancellation-feedback" /></div>
          </Route>
          <Route path="/admin/reported-questions">
            <AdminQuestionReports />
          </Route>
          <Route path="/admin/feedback">
            <AdminFeedback />
          </Route>
          <Route path="/admin/api-keys">
            <AdminApiKeys />
          </Route>
          <Route path="/admin/activity">
            <div className="p-6 space-y-6"><AdminSection section="activity" /></div>
          </Route>
          <Route path="/admin/prize-management">
            <div className="p-6 space-y-6"><AdminSection section="campaigns" /></div>
          </Route>
          <Route path="/admin/discounts">
            <div className="p-6 space-y-6"><DiscountCodeManager /></div>
          </Route>
          <Route path="/admin/vouchers">
            <div className="p-6 space-y-6"><AdminVouchers /></div>
          </Route>
          <Route path="/admin/referrals">
            <div className="p-6 space-y-6"><ReferralPayoutManager /></div>
          </Route>
          <Route path="/admin/lessons">
            <div className="p-6 space-y-6"><AdminLessons /></div>
          </Route>
          <Route path="/admin/performance-tasks">
            <div className="p-6 space-y-6"><AdminPerformanceTasks /></div>
          </Route>
          <Route path="/admin/moat">
            <div className="p-6 space-y-6"><AdminMoat /></div>
          </Route>
          <Route path="/admin/sign-in">
            <div className="p-6 space-y-6"><AdminSignIn /></div>
          </Route>
          <Route path="/admin/courses">
            <div className="p-6 space-y-6"><AdminCourses /></div>
          </Route>
          <Route path="/admin/comms">
            <div className="p-6 space-y-6"><AdminComms /></div>
          </Route>
          <Route path="/admin/reading-materials">
            <div className="p-6 space-y-6"><ReadingMaterialsManager /></div>
          </Route>
          <Route path="/admin/curriculum">
            <div className="p-6 space-y-6"><CurriculumManager /></div>
          </Route>
          <Route path="/admin/academic-calendar">
            <div className="p-6 space-y-6"><AcademicCalendarManager /></div>
          </Route>
          <Route path="/admin/brevo-mappings">
            <div className="p-6 space-y-6"><BrevoMappingManager /></div>
          </Route>
          <Route path="/admin/materials">
            <div className="p-6 space-y-6"><LearningMaterialsManager /></div>
          </Route>
          <Route path="/admin/parents">
            <div className="p-6 space-y-6"><AdminParents /></div>
          </Route>
          <Route path="/admin/banners">
            <div className="p-6 space-y-6"><AdminBanners /></div>
          </Route>
          <Route path="/admin/announcements">
            <AdminAnnouncements />
          </Route>
          <Route path="/admin/live-classes">
            <div className="p-6 space-y-6"><AdminLiveClasses /></div>
          </Route>
          <Route path="/admin/knowledge-base">
            <AdminKnowledgeBase />
          </Route>
          <Route path="/admin/whatsapp">
            <div className="p-6 space-y-6"><WhatsAppManager /></div>
          </Route>
          <Route path="/admin/whatsapp-inbox">
            <WhatsAppInbox />
          </Route>
          <Route path="/admin/stripe-pricing">
            <div className="p-6 space-y-6"><AdminStripePricing /></div>
          </Route>
          <Route path="/admin/xp-decay">
            <div className="p-6 space-y-6"><AdminXPDecay /></div>
          </Route>
          <Route path="/admin/pep-predictions">
            <div className="p-6 space-y-6"><AdminPepPredictions /></div>
          </Route>
          <Route path="/admin/creator-applications">
            <div className="p-6 space-y-6"><AdminCreatorApplications /></div>
          </Route>
          <Route path="/admin/creator-videos">
            <div className="p-6 space-y-6"><AdminCreatorVideos /></div>
          </Route>
          <Route path="/admin/user/:userId/report">
            {(params: { userId: string }) => (
              <div className="p-6 space-y-6">
                <ProgressReport mode="admin" adminUserId={params.userId} />
              </div>
            )}
          </Route>
          <Route path="/admin/dummy-accounts">
            <div className="p-6 space-y-6"><AdminSection section="dummy-accounts" /></div>
          </Route>
          <Route>
            <AdminHome />
          </Route>
        </Switch>
      </AdminLayout>
    </ProtectedAdminRoute>
  );
}

function MainRouter() {
  return (
    <Switch>
      <Route path="/" component={Home} />
      <Route path="/signup" component={Signup} />
      <Route path="/login" component={Login} />
      <Route path="/forgot-password" component={ForgotPassword} />
      <Route path="/reset-password" component={ResetPassword} />
      <Route path="/claim-account" component={ClaimAccount} />
      <Route path="/find-account" component={FindAccount} />
      <Route path="/onboard/:token" component={Onboarding} />
      <Route path="/contact" component={ContactPage} />
      <Route path="/features" component={FeaturesPage} />
      <Route path="/how-it-works" component={HowItWorksPage} />
      <Route path="/preferences" component={Preferences} />
      <Route path="/privacy" component={Privacy} />
      <Route path="/terms-conditions" component={TermsConditions} />
      <Route path="/api-docs" component={ApiDocs} />
      <Route path="/trial/:slug" component={TrialLink} />
      <Route path="/summer-2026" component={SummerInterest} />
      <Route path="/dashboard">
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      </Route>
      <Route path="/study-plan">
        <ProtectedRoute>
          <StudyPlan />
        </ProtectedRoute>
      </Route>
      <Route path="/learning">
        <ProtectedRoute>
          <Learning />
        </ProtectedRoute>
      </Route>
      <Route path="/results">
        <ProtectedRoute>
          <TestResults />
        </ProtectedRoute>
      </Route>
      <Route path="/messages">
        <ProtectedRoute>
          <Messages />
        </ProtectedRoute>
      </Route>
      <Route path="/notifications">
        <ProtectedRoute>
          <Notifications />
        </ProtectedRoute>
      </Route>
      <Route path="/user/:userId">
        <ProtectedRoute>
          <UserProfile />
        </ProtectedRoute>
      </Route>
      <Route path="/subscribe" component={Subscribe} />
      {/* Public lead magnet: no auth, no nav chrome. See server/services/leadCheck.ts. */}
      <Route path="/pep-check" component={PepCheck} />
      {/* Paid Meta traffic lands here. Public, and it creates the account in the page. */}
      <Route path="/pep-prep" component={PepPrep} />
      <Route path="/practice-papers" component={PracticePapers} />
      <Route path="/practice-papers/download/:token">
        {(params) => <PaperDownloadSuccess params={params} />}
      </Route>
      <Route path="/settings" component={ProfileSettings} />
      <Route path="/profile">
        <ProtectedRoute>
          <MyProfile />
        </ProtectedRoute>
      </Route>
      <Route path="/report-card">
        <ProtectedRoute>
          <ProgressReport mode="student" />
        </ProtectedRoute>
      </Route>
      <Route path="/progress-report">
        <ProtectedRoute>
          <ProgressReport mode="student" />
        </ProtectedRoute>
      </Route>
      {/* Auth-only: lapsed students must still be able to report their real PEP results. */}
      <Route path="/pep-results">
        <AuthOnlyRoute>
          <PepResults />
        </AuthOnlyRoute>
      </Route>
      {/* Auth-only: a lapsed subscriber can still work through an interest course. */}
      <Route path="/courses">
        <AuthOnlyRoute>
          <Courses />
        </AuthOnlyRoute>
      </Route>
      <Route path="/courses/:slug">
        <AuthOnlyRoute>
          <CourseDetail />
        </AuthOnlyRoute>
      </Route>
      {/* Public and shareable: a credential link must open for anyone, signed in or not. */}
      <Route path="/credentials/:code" component={CredentialPage} />
      <Route path="/challenge/:id">
        <ProtectedRoute>
          <ChallengeQuiz />
        </ProtectedRoute>
      </Route>
      <Route path="/challenge/:id/results">
        <ProtectedRoute>
          <ChallengeResults />
        </ProtectedRoute>
      </Route>
      <Route path="/claim-prize">
        <ProtectedRoute>
          <ClaimPrize />
        </ProtectedRoute>
      </Route>
      <Route path="/prizes">
        <Redirect to="/claim-prize" />
      </Route>
      <Route path="/leaderboard">
        <ProtectedRoute>
          <Leaderboard />
        </ProtectedRoute>
      </Route>
      <Route path="/social">
        <ProtectedRoute>
          <FollowsPage />
        </ProtectedRoute>
      </Route>
      <Route path="/feed">
        <ProtectedRoute>
          <ActivityFeed />
        </ProtectedRoute>
      </Route>
      <Route path="/classes">
        <ProtectedRoute>
          <Classes />
        </ProtectedRoute>
      </Route>
      <Route path="/classes/:id">
        <ProtectedRoute>
          <ClassDetail />
        </ProtectedRoute>
      </Route>
      <Route path="/badges">
        <ProtectedRoute>
          <ViewAllBadges />
        </ProtectedRoute>
      </Route>
      <Route path="/lessons">
        <ProtectedRoute>
          <LessonLibrary />
        </ProtectedRoute>
      </Route>
      <Route path="/performance-tasks">
        <ProtectedRoute>
          <PerformanceTasks />
        </ProtectedRoute>
      </Route>
      <Route path="/performance-tasks/:slug">
        <ProtectedRoute>
          <PerformanceTaskPlayer />
        </ProtectedRoute>
      </Route>
      <Route path="/lessons/:slug">
        <ProtectedRoute>
          <LessonPlayer />
        </ProtectedRoute>
      </Route>
      <Route path="/reading">
        <ProtectedRoute>
          <ReadingLibrary />
        </ProtectedRoute>
      </Route>
      <Route path="/reading/:slug">
        <ProtectedRoute>
          <ReadingView />
        </ProtectedRoute>
      </Route>
      <Route path="/ref/login" component={PartnerLogin} />
      <Route path="/ref/signup" component={PartnerSignup} />
      <Route path="/ref/dashboard" component={PartnerDashboard} />
      <Route path="/creator/complete-signup" component={CreatorCompleteSignup} />
      <Route path="/creator/courses">
        <AuthOnlyRoute>
          <CreatorCourses />
        </AuthOnlyRoute>
      </Route>
      <Route path="/parent/login" component={ParentLogin} />
      <Route path="/parent" component={ParentDashboardPage} />
      <Route path="/parent/children" component={ParentChildren} />
      <Route path="/parent/children/:childId" component={ParentChildDetail} />
      <Route path="/parent/children/:childId/report">
        {(params: { childId: string }) => <ProgressReport mode="parent" childId={params.childId} />}
      </Route>
      <Route path="/parent/billing" component={ParentBilling} />
      <Route path="/parent/notifications" component={ParentNotifications} />
      <Route path="/parent/settings" component={ParentSettings} />
      {/* Legacy paths kept as redirects so any stale link still lands somewhere sensible. */}
      <Route path="/parent/dashboard" component={ParentDashboardPage} />
      <Route path="/parent/child/:childId">
        {(params: { childId: string }) => <ParentChildDetail />}
      </Route>
      <Route component={NotFound} />
    </Switch>
  );
}

const PAGE_TITLES: Record<string, string> = {
  "/":                    "EduFocal Academy | #1 PEP Exam Prep",
  "/features":            "Features | EduFocal Academy",
  "/how-it-works":        "How It Works | EduFocal Academy",
  "/contact":             "Contact Us | EduFocal Academy",
  "/preferences":         "Email Preferences | EduFocal Academy",
  "/privacy":             "Privacy Policy | EduFocal Academy",
  "/terms-conditions":    "Terms & Conditions | EduFocal Academy",
  "/signup":              "Sign Up | EduFocal Academy",
  "/login":               "Sign In | EduFocal Academy",
  "/forgot-password":     "Forgot Password | EduFocal Academy",
  "/find-account":        "Find Your Account | EduFocal Academy",
  "/claim-account":       "Claim Your Account | EduFocal Academy",
  "/dashboard":           "Dashboard | EduFocal Academy",
  "/study-plan":          "Study Plan | EduFocal Academy",
  "/learning":            "Learning | EduFocal Academy",
  "/results":             "Results | EduFocal Academy",
  "/messages":            "Messages | EduFocal Academy",
  "/notifications":       "Notifications | EduFocal Academy",
  "/leaderboard":         "Leaderboard | EduFocal Academy",
  "/pep-results":         "PEP Results | EduFocal Academy",
  "/pep-prep":            "PEP Practice for Grades 4, 5 and 6 | EduFocal Academy",
  "/courses":             "Courses | EduFocal Academy",
  "/profile":             "My Profile | EduFocal Academy",
  "/settings":            "Settings | EduFocal Academy",
  "/subscription":        "Subscription | EduFocal Academy",
  "/parent":              "Parent Dashboard | EduFocal Academy",
  "/parent/dashboard":    "Parent Dashboard | EduFocal Academy",
  "/parent/children":     "My Children | EduFocal Academy",
  "/parent/billing":      "Billing | Parent Dashboard",
  "/parent/notifications": "Notifications | Parent Dashboard",
  "/parent/settings":     "Settings | Parent Dashboard",
  "/admin":               "Admin | EduFocal Academy",
  "/admin/moat":          "Data Moat | EduFocal Academy",
  "/admin/sign-in":       "Sign-in Health | EduFocal Academy",
  "/admin/courses":       "Courses | EduFocal Academy",
  "/admin/comms":         "Comms | EduFocal Academy",
  "/admin/metrics":       "Metrics | EduFocal Academy",
  "/admin/acquisition":   "Acquisition | EduFocal Academy",
  "/creator/courses":     "My Courses | EduFocal Academy",
};

function getPageTitle(location: string): string {
  if (PAGE_TITLES[location]) return PAGE_TITLES[location];
  // Prefix matches
  if (location.startsWith("/courses"))     return "Courses | EduFocal Academy";
  if (location.startsWith("/credentials")) return "Certificate | EduFocal Academy";
  if (location.startsWith("/admin"))   return "Admin | EduFocal Academy";
  if (location.startsWith("/parent"))  return "Parent Portal | EduFocal Academy";
  if (location.startsWith("/ref"))     return "Partner Portal | EduFocal Academy";
  if (location.startsWith("/trial"))   return "Trial | EduFocal Academy";
  if (location.startsWith("/pep-check")) return "Free PEP Readiness Check | EduFocal Academy";
  if (location.startsWith("/onboard")) return "Onboarding | EduFocal Academy";
  return "EduFocal Academy";
}

function AppContent() {
  const { user, isLoading } = useAuth();
  const [location] = useLocation();
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const isAdminRoute = location.startsWith("/admin");
  const isParentRoute = location.startsWith("/parent");
  const isStandaloneRoute = location === "/subscribe" || location === "/settings" || location === "/preferences" || location === "/pep-check";

  useEffect(() => {
    document.title = getPageTitle(location);
  }, [location]);

  useEffect(() => {
    if (!isLoading && user) {
      const validSubscriptionStatuses = ['active', 'trial', 'trialing', 'past_due'];
      const hasActiveSubscription = validSubscriptionStatuses.includes(user.subscriptionStatus || '');
      const needsSetup = hasActiveSubscription && user.role !== 'admin' && (
        !user.username || !user.parentFirstName || !user.parentLastName || !user.parentEmail
      );
      setShowUsernameModal(needsSetup);
    } else if (!isLoading && !user) {
      setShowUsernameModal(false);
    }
  }, [user, isLoading]);

  if (isAdminRoute) {
    return (
      <>
        <PreferencesSyncer />
        <ReferralTracker />
        <AdminRouter />
        <Toaster />
        <LiveActivityToast />
      </>
    );
  }

  if (isParentRoute) {
    return (
      <>
        <PreferencesSyncer />
        <MainRouter />
        <Toaster />
        <LiveActivityToast />
      </>
    );
  }

  if (isStandaloneRoute) {
    return (
      <>
        <PreferencesSyncer />
        <MainRouter />
        <Toaster />
        <LiveActivityToast />
      </>
    );
  }

  const deletionDate = user?.deletionRequestedAt
    ? new Date(new Date(user.deletionRequestedAt).getTime() + 15 * 24 * 60 * 60 * 1000)
    : null;

  return (
    <>
      <PreferencesSyncer />
      <ReferralTracker />
      <div className="min-h-screen bg-background">
        {!isEmbedded() && <TopNavigation />}
        {deletionDate && (
          <div className="bg-destructive text-destructive-foreground text-center py-2 px-4 text-sm">
            Your account will be deleted on{' '}
            {deletionDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}.{' '}
            <a href="/settings?tab=billing" className="underline font-medium">
              Go to Settings to cancel
            </a>.
          </div>
        )}
        <main className="container mx-auto pb-16 md:pb-0">
          <MainRouter />
        </main>
        <MobileBottomNav />
        {!isEmbedded() && <StudyBuddy />}
      </div>
      <Toaster />
      <LiveActivityToast />
      {!isEmbedded() && (
        <>
          <UsernameSelectionModal
            open={showUsernameModal}
            onClose={() => setShowUsernameModal(false)}
          />
          <AnnouncementModal />
          <GradeMigrationModal />
          <PwaInstallModal />
          <PushPermissionModal />
        </>
      )}
    </>
  );
}

const MARKETING_ROUTES = ['/', '/features', '/how-it-works', '/contact', '/privacy', '/terms-conditions', '/signup', '/login', '/forgot-password', '/find-account', '/subscribe', '/preferences', '/pep-check', '/pep-prep'];

function App() {
  const [location] = useLocation();
  const isMarketingPage = MARKETING_ROUTES.includes(location) || location.startsWith('/reset-password') || location.startsWith('/claim-account') || location.startsWith('/onboard') || location.startsWith('/trial');

  useEffect(() => {
    initMixpanel();
    initPostHog();
    initMetaPixel();
  }, []);

  useEffect(() => {
    capturePostHogPageview();
    metaPageView();
  }, [location]);

  return (
    <QueryClientProvider client={queryClient}>
      <TerritoryProvider>
      <AuthProvider>
        <WebSocketProvider>
        <AccessibilityProvider>
          <ThemeProvider defaultTheme="light" storageKey="edufocal-theme" forcedTheme={isMarketingPage ? 'light' : undefined}>
            <TooltipProvider>
              <AppContent />
              {/* Once, above every layout branch: see SupportWidget for why. */}
              <SupportWidget />
            </TooltipProvider>
          </ThemeProvider>
        </AccessibilityProvider>
        </WebSocketProvider>
      </AuthProvider>
      </TerritoryProvider>
    </QueryClientProvider>
  );
}

export default App;
